import { useFieldArray, useFormContext } from 'react-hook-form';
import SingleBtn from 'components/common/elements/SingleBtn';
import CustomDatePicker from 'components/common/display/CustomDatepicker';
import Dropdown from 'components/common/elements/Dropdown';
import FormMediaFile from 'components/common/forms/FormMediaFile';
import FormAutoHeightTextarea from 'components/common/forms/FormAutoHeightTextarea';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import styles from 'styles/pages/chart/DHImplementationChart.module.scss';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import {
  assessmentLists,
  assessmentLimits,
  DHImplementationChartFormDefaultValues,
} from 'constants/chart/dhImplementationChart';
import { TAttachmentFiles } from 'types/chart/dhImplementationChart';
import { TSelectObject } from 'types/dropdown';
import useModal from 'hooks/useModal';

type TProps = {
  formMediaFileRef: React.MutableRefObject<{ resetFile: () => void } | null>;
  attachmentFiles: TAttachmentFiles;
  setAttachmentFiles: React.Dispatch<React.SetStateAction<TAttachmentFiles>>;
  deleteFileIdList: number[];
  setDeleteFileIdList: React.Dispatch<React.SetStateAction<number[]>>;
  changeFile: (index: number, file: File[]) => void;
  showAssessmentList: boolean[];
  setShowAssessmentList: React.Dispatch<React.SetStateAction<boolean[]>>;
  date: Date[];
  setDate: React.Dispatch<React.SetStateAction<Date[]>>;
  selectedObject: TSelectObject[];
  setSelectedObject: React.Dispatch<React.SetStateAction<TSelectObject[]>>;
  isRegister: boolean;
  isReadOnly: boolean;
  fields: Record<string, any>[];
  append: (value: any) => void;
  remove: (index: number) => void;
};

const DHImplementationChart = ({
  formMediaFileRef,
  attachmentFiles,
  setAttachmentFiles,
  deleteFileIdList,
  setDeleteFileIdList,
  changeFile,
  showAssessmentList,
  setShowAssessmentList,
  date,
  setDate,
  selectedObject,
  setSelectedObject,
  isRegister,
  isReadOnly,
  fields,
  append,
  remove,
}: TProps) => {
  const { openModal } = useModal();

  const { register, setValue, watch } = useFormContext();

  const fieldWatch = watch('field');
  const assessments = fieldWatch
    ? fieldWatch.map((field: any) => field.assessment)
    : [];

  const addField = () => {
    if (fields.length < 9) {
      setDate((prev) => {
        const newDate = [...prev];
        newDate.push(new Date());
        return newDate;
      });
      setShowAssessmentList((prev) => {
        const newShowList = [...prev];
        newShowList.push(false);
        return newShowList;
      });
      setAttachmentFiles((prev) => {
        const newFiles = { ...prev };
        newFiles[fields.length] = null;
        return newFiles;
      });
      append(DHImplementationChartFormDefaultValues);
    } else {
      openModal({
        text: '9개 이상 작성이 불가합니다.',
      });
    }
  };

  const removeField = (index: number) => {
    remove(index);
    setShowAssessmentList((prev) => {
      const newShowList = [...prev];
      newShowList.splice(index, 1);
      return newShowList;
    });
    setAttachmentFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[index];
      return newFiles;
    });
  };

  const changeDatePicker = (date: Date, index: number) => {
    setDate((prev) => {
      const newDateList = [...prev];
      newDateList[index] = date;
      return newDateList;
    });
    setValue(`field.${index}.date` as const, date as never);
  };

  const clickDropdown = (index: number) => {
    setShowAssessmentList((prev) => {
      const newShowList = [...prev];
      newShowList[index] = !prev[index];
      return newShowList;
    });
  };

  const changeDropdown = (index: number, selectObject: TSelectObject) => {
    setShowAssessmentList((prev) => {
      const newShowList = [...prev];
      newShowList[index] = false;
      return newShowList;
    });
    if (!selectObject) return;

    const count =
      assessments.filter((value: any) => value === selectObject.value).length +
      1;
    const limit = assessmentLimits[selectObject.label];

    if (count > limit) {
      openModal({
        text: `${limit}개 이상 작성이 불가합니다.`,
      });
      return;
    }
    setSelectedObject((prev) => {
      const newSelectedObject = { ...prev };
      newSelectedObject[index] = selectObject;
      return newSelectedObject;
    });
    setValue(`field.${index}.assessment` as const, selectObject.value as never);
  };

  return (
    <>
      {isRegister && !isReadOnly && (
        <div className="flex justify_end">
          <SingleBtn
            btnText="추가하기"
            icon={
              <PlusIcon width={20} height={20} fill={STYLES_VARIABLES.WHITE} />
            }
            isBtnActive={true}
            btnStyle={{
              display: 'flex',
              alignItems: 'center',
              gap: '11px',
              padding: '10px 16px',
            }}
            onClick={addField}
          />
        </div>
      )}
      {fields.map((field, index) => (
        <div key={field.id} className={styles.chart_wrapper}>
          {index > 0 && isRegister && !isReadOnly && (
            <div className={styles.delete}>
              <button type="button" onClick={() => removeField(index)}>
                <CloseIcon
                  width={24}
                  height={24}
                  fill={STYLES_VARIABLES.GRAY_07}
                />
              </button>
            </div>
          )}
          <div className={styles.m24}>
            <div className="flex gap24">
              <div>
                <label className={styles.label}>Date</label>
                <CustomDatePicker
                  date={date[index]}
                  calendarStyle={{
                    width: '224px',
                    padding: '12px 16px',
                    fontSize: '16px',
                    height: '48px',
                  }}
                  leftIconFill={STYLES_VARIABLES.GRAY_08}
                  rightIconFill={STYLES_VARIABLES.GRAY_08}
                  maxDate={new Date()}
                  readOnly={!isRegister || isReadOnly}
                  onChange={(date: Date) => changeDatePicker(date, index)}
                />
              </div>
              <div className="w100">
                <label htmlFor="location" className={styles.label}>
                  Location
                </label>
                <input
                  {...register(`field.${index}.location` as const)}
                  id="location"
                  type="text"
                  maxLength={50}
                  readOnly={!isRegister || isReadOnly}
                  className={styles.input}
                  placeholder="텍스트를 입력해 주세요."
                />
              </div>
            </div>
            <div className={styles.divider_line}></div>
            <div className="w100">
              <label
                style={{
                  display: 'block',
                  color: `${STYLES_VARIABLES.GRAY_07}`,
                  marginBottom: '8px',
                }}
              >
                치위생수행 항목 선택
              </label>
              <Dropdown
                selectList={assessmentLists}
                selectedObject={selectedObject[index] || null}
                showSelectList={showAssessmentList[index]}
                btnStyle={{
                  width: '100%',
                  height: '48px',
                  border: `1px solid ${STYLES_VARIABLES.GRAY_05}`,
                  padding: '12px 24px',
                  fontSize: '16px',
                }}
                dropdownStyle={{ width: '870px' }}
                readOnly={!isRegister || isReadOnly}
                onClickButton={() => clickDropdown(index)}
                onClickSelectItem={(value) => changeDropdown(index, value)}
                placeholder="치위생수행 항목을 선택해 주세요. "
              />
            </div>
            <div className={styles.divider_line}></div>
            <div className="w100">
              <label
                htmlFor="memo"
                style={{
                  display: 'block',
                  color: `${STYLES_VARIABLES.GRAY_07}`,
                  marginBottom: '8px',
                }}
              >
                치위생수행 과정 기록 및 사진
              </label>
              <FormAutoHeightTextarea
                name={`field.${index}.memo`}
                placeholder="텍스트를 입력해 주세요."
                readOnly={!isRegister || isReadOnly}
                textareaStyle={{
                  width: '100%',
                  minHeight: '96px',
                  border: '1px solid #ced4da',
                  borderRadius: '8px',
                  marginLeft: '0',
                  padding: '12px 16px',
                }}
              />
            </div>
            <FormMediaFile
              ref={formMediaFileRef}
              files={attachmentFiles[index] || []}
              multiple={true}
              maxLength={4}
              inputId={`DHImages${index}`}
              className="mt8"
              readOnly={!isRegister || isReadOnly}
              onDelete={(index) =>
                setDeleteFileIdList([...deleteFileIdList, index])
              }
              onChange={(files) => changeFile(index, files)}
            />
          </div>
        </div>
      ))}
    </>
  );
};

export default DHImplementationChart;
